"use client";

import { motion } from "framer-motion";
import { ArrowUp, Mail, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { navItems } from "./navbar";
import Link from "next/link";
import { useLocale } from "next-intl";
import Image from "next/image";

export default function Footer() {
  const [showTop, setShowTop] = useState(false);
  const locale = useLocale();

  const address =
    locale === "en"
      ? "N1 - District 4811, Said Zakaria St, Sheraton, Cairo, Egypt"
      : "المبنى N1 - الحي 4811، شارع سعيد زكريا، شيراتون، القاهرة، مصر";

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="relative bg-linear-to-b from-[#081827] via-[#0a1b2e] to-[#07121f] text-slate-300 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-32 left-1/4 w-md h-112 bg-[#c9a24d]/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-0 w-lg h-128 bg-[#132b44]/70 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-6 py-24">
        {/* Top Section */}
        <div className="grid md:grid-cols-3 gap-16 mb-20">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <Image
              src="/dark-logo.png"
              alt="LogicHub Logo"
              width={180}
              height={45}
              className="mb-8"
            />
            <p className="leading-relaxed text-slate-400 max-w-md">
              {locale === "en"
                ? `Engineering operational clarity through structured Odoo ERP implementations across Egypt and the Middle East.`
                : `تحقيق الوضوح التشغيلي من خلال تنفيذات منظمة لنظام أودو ERP في مصر والشرق الأوسط.`}
            </p>
          </motion.div>

          {/* Links */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.7, ease: "easeOut" }}
            className="flex justify-center"
          >
            <div>
              <h4 className="text-xl font-semibold text-white mb-6">
                {locale === "en" ? "Quick Links" : "روابط سريعة"}
              </h4>
              <ul className="space-y-4">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="group relative text-sm tracking-wide cursor-pointer"
                    >
                      <span className="transition group-hover:text-[#e6c77a]">
                        {locale === "en" ? item.label : item.labelAr}
                      </span>
                      <span className="absolute left-0 -bottom-1 h-px w-0 bg-[#e6c77a] transition-all duration-300 group-hover:w-full" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.7, ease: "easeOut" }}
            className="flex justify-center"
          >
            <div>
              <h4 className="text-xl font-semibold text-white mb-6">
                {locale === "en" ? "Contact" : "تواصل معنا"}
              </h4>

              <div className="space-y-5">
                <Link
                  href="mailto:info@logichubeg.com"
                  className="flex items-center gap-3 text-sm hover:text-[#e6c77a] transition"
                >
                  <Mail size={18} />
                  info@logichubeg.com
                </Link>

                <div className="flex items-start gap-3 text-sm text-slate-400">
                  <MapPin size={18} className="mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{address}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 mb-10" />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-3 text-center md:text-left">
            <p className="text-sm text-slate-500">
              {locale === "en"
                ? `© ${new Date().getFullYear()} LogicHub. All rights reserved.`
                : `© ${new Date().getFullYear()} جميع الحقوق محفوظة لوجيكهب.`}
            </p>
            <p className="text-sm text-slate-500">
              {locale === "en"
                ? "Designed & Developed by"
                : "تصميم وتطوير من قبل"}
            </p>
            <Link
              href="https://novi-ms.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition"
            >
              <Image src="/footer.webp" alt="footer" width={34} height={34} />
            </Link>
          </div>

          {/* Back to Top */}
          <motion.button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: showTop ? 1 : 0,
              scale: showTop ? 1 : 0.85,
            }}
            transition={{ duration: 0.25 }}
            className={`relative p-3 rounded-full border border-[#c9a24d]/40 text-[#c9a24d] cursor-pointer
              hover:bg-[#c9a24d] hover:text-[#07121f] transition-all shadow-lg
              ${showTop ? "pointer-events-auto" : "pointer-events-none"}`}
          >
            <ArrowUp size={18} />
            <span className="absolute inset-0 rounded-full blur-md bg-[#c9a24d]/20 -z-10" />
          </motion.button>
        </div>
      </div>
    </footer>
  );
}
